import { xMaxAbsoluteValue } from 'ml-spectra-processing';
import { getSumOfShapes } from "./shapes/getSumOfShapes.js";
import { getFixedParametersResult } from "./util/getFixedParametersResult.js";
import { getGlobalParameterVectors } from "./util/getGlobalParameterVectors.js";
import { getInternalPeaks } from "./util/internalPeaks/getInternalPeaks.js";
import { selectMethod } from "./util/selectMethod.js";
/**
 * Fits a set of points to the sum of a set of bell functions.
 *
 * @param data - An object containing the x and y data to be fitted.
 * @param peaks - A list of initial parameters to be optimized. e.g. coming from a peak picking [{x, y, width}].
 * @param options - Options for optimize
 * @returns - An object with fitting error and the list of optimized parameters { parameters: [ {x, y, width} ], error } if the kind of shape is pseudoVoigt mu parameter is optimized.
 */
export function optimize(data, peaks, options = {}) {
    // rescale data so the maximum Y value becomes 1
    const max = xMaxAbsoluteValue(data.y);
    const yScale = max === 0 ? 1 : max;
    const internalPeaks = getInternalPeaks(peaks, yScale, options);
    // need to rescale what is related to Y
    const normalizedY = new Float64Array(data.y.length);
    for (let i = 0; i < data.y.length; i++) {
        normalizedY[i] = data.y[i] / yScale;
    }
    const { freeIndices, globalMin, globalMax, globalInit, globalGrad } = getGlobalParameterVectors(internalPeaks, peaks, options);
    const nbParams = globalInit.length;
    const { algorithm, optimizationOptions } = selectMethod(options.optimization);
    const baseSumOfShapes = getSumOfShapes(internalPeaks);
    if (freeIndices.length === 0) {
        return getFixedParametersResult(internalPeaks, normalizedY, data.x, globalInit, baseSumOfShapes, yScale);
    }
    // prepare arrays to pass to the algorithm (reduced if needed)
    let minValues;
    let maxValues;
    let initialValues;
    let gradientDifferences;
    let sumOfShapesToUse = baseSumOfShapes;
    if (freeIndices.length === nbParams) {
        // nothing to reduce
        minValues = globalMin;
        maxValues = globalMax;
        initialValues = globalInit;
        gradientDifferences = globalGrad;
    }
    else {
        // wrapper that maps reduced (free) parameters into the full parameter vector
        const sumOfShapesForReduced = (reducedParameters) => {
            const full = new Float64Array(nbParams);
            full.set(globalInit);
            for (let k = 0; k < freeIndices.length; k++) {
                full[freeIndices[k]] = reducedParameters[k];
            }
            return baseSumOfShapes(Array.from(full));
        };
        minValues = new Float64Array(freeIndices.length);
        maxValues = new Float64Array(freeIndices.length);
        initialValues = new Float64Array(freeIndices.length);
        gradientDifferences = new Float64Array(freeIndices.length);
        for (let j = 0; j < freeIndices.length; j++) {
            const i = freeIndices[j];
            minValues[j] = globalMin[i];
            maxValues[j] = globalMax[i];
            initialValues[j] = globalInit[i];
            gradientDifferences[j] = globalGrad[i];
        }
        sumOfShapesToUse = sumOfShapesForReduced;
    }
    const fitted = algorithm({ x: data.x, y: normalizedY }, sumOfShapesToUse, {
        minValues,
        maxValues,
        initialValues,
        gradientDifference: gradientDifferences,
        ...optimizationOptions,
    });
    // reconstruct full parameter vector
    let fittedValues;
    if (freeIndices.length === nbParams) {
        fittedValues = fitted.parameterValues;
    }
    else {
        const full = Array.from(globalInit);
        for (let k = 0; k < freeIndices.length; k++) {
            full[freeIndices[k]] = fitted.parameterValues[k];
        }
        fittedValues = full;
    }
    const newPeaks = [];
    for (const peak of internalPeaks) {
        const { id, shape, parameters, fromIndex } = peak;
        let newPeak = { x: 0, y: 0, shape };
        if (id) {
            newPeak = { ...newPeak, id };
        }
        newPeak.x = fittedValues[fromIndex];
        newPeak.y = fittedValues[fromIndex + 1] * yScale;
        for (let i = 2; i < parameters.length; i++) {
            //@ts-expect-error should be fixed once
            newPeak.shape[parameters[i]] = fittedValues[fromIndex + i];
        }
        newPeaks.push(newPeak);
    }
    return {
        error: fitted.parameterError,
        iterations: fitted.iterations,
        peaks: newPeaks,
    };
}
//# sourceMappingURL=index.js.map